import type { ReactNode } from "react";
import { cn } from "./cn";
import { Ikon, type IkonAdi } from "./Ikon";

/**
 * SEKMELER — aynı listenin farklı dilimleri
 *
 * ================================================================
 * NEDEN BİLEŞEN OLDU
 * ================================================================
 * Aynı çip yedi tuvalde geçiyor (`Personel` · `Ogrenciler` ·
 * `Mufredat` · `Takvim` · `KuyrukEkranlari` · `DurumMatrisi` ·
 * `OgrenciBaglami`) ve panelde üç ekran onu `Buton gorunum="birincil"`
 * / `"sessiz"` ikilisiyle taklit ediyordu. Taklit yakındı ama aynı
 * değildi: düğme 14px/500 ve dolgu gradyanlı, çizimdeki çip 13px/500
 * ve `marka-50` zeminli.
 *
 * ================================================================
 * `role="tab"` DEĞİL, `aria-pressed` — VE BU BİLİNÇLİ
 * ================================================================
 * ARIA sekme deseni yalnızca `role="tab"` yazmakla kurulmuyor: ok
 * tuşlarıyla gezinme, gezinen odak (`tabindex="-1"` + roving) ve her
 * sekmenin bir `tabpanel`e bağlanması gerekiyor. Yarısını yazmak,
 * hiç yazmamaktan KÖTÜ: ekran okuyucu kullanıcısına "burada ok
 * tuşlarıyla gezebilirsin" diye söz verilir ve o söz tutulmaz.
 *
 * Bu çipler zaten bir panel değiştirmiyor; AYNI listeyi süzüyorlar.
 * Basılı kalan düğme (`aria-pressed`) tam olarak bunu anlatıyor ve
 * klavyede sekme tuşu doğal biçimde çalışıyor.
 *
 * ================================================================
 * SAYI İSTEĞE BAĞLI — VE YOKLUĞU "SIFIR" DEMEK DEĞİL
 * ================================================================
 * `sayi` verilmezse çip yalnızca adını yazıyor. Bilinmeyen bir sayıyı
 * sıfır göstermek, bu depodaki "ölçülmedi ≠ sıfır" kuralının en ucuz
 * ihlali olurdu: kullanıcı arşivde kimse olmadığını sanır.
 */

export interface Sekme<T extends string> {
  deger: T;
  etiket: string;
  /** Çizimdeki "Aktif · 8" sayısı. Bilinmiyorsa VERİLMEZ. */
  sayi?: number;
}

export interface SekmelerProps<T extends string> {
  sekmeler: readonly Sekme<T>[];
  secili: T;
  onSec: (deger: T) => void;
  /**
   * Grubun ekran okuyucuya verilen adı — "Personel listesi dilimleri".
   *
   * Zorunlu: adsız bir düğme grubu, ekran okuyucuda birbiriyle ilgisiz
   * iki düğme gibi duyuruluyor ve kullanıcı bunların aynı listeyi
   * süzdüğünü anlamıyor.
   */
  etiket: string;
  /**
   * Görünüm. `hap`: UNİKOÇ sekme hapı (sürüm 9a, tarama-1 §2.7) — aktif = degrade dolu hap + beyaz
   * yazı, pasif = beyaz hap + ince kenar. Varsayılan `cip`: mevcut görünüm değişmez.
   */
  gorunum?: "cip" | "hap";
  className?: string;
}

export function Sekmeler<T extends string>({
  sekmeler,
  secili,
  onSec,
  etiket,
  gorunum = "cip",
  className,
}: SekmelerProps<T>) {
  const hap = gorunum === "hap";
  return (
    <div
      role="group"
      aria-label={etiket}
      className={cn("flex flex-wrap", hap ? "gap-2" : "gap-1.5", className)}
    >
      {sekmeler.map((s) => {
        const acik = s.deger === secili;
        return (
          <button
            key={s.deger}
            type="button"
            aria-pressed={acik}
            onClick={() => onSec(s.deger)}
            className={cn(
              // Olculer cizimden: 44px dokunma yuksekligi, 13px yan
              // dolgu, 13px/500 yazi.
              "min-h-dokunma inline-flex items-center",
              hap
                ? "rounded-rozet gap-1.5 px-4 text-sm font-semibold"
                : "rounded-alan px-[13px] text-[13px] font-medium",
              "ease-cikis transition-[box-shadow,background-color,color] duration-(--sure-anlik)",
              hap
                ? acik
                  ? "text-notr-0 bg-(image:--gradyan-unk-birincil) shadow-(--golge-unk-birincil)"
                  : "bg-notr-0 text-metin-koyu ring-mor-kenar hover:bg-mor-50 ring-1 ring-inset"
                : acik
                  ? "bg-marka-50 text-marka-700 ring-marka-100 ring-1 ring-inset"
                  : "text-notr-700 hover:bg-notr-100",
            )}
          >
            {s.etiket}
            {/*
              Sayi AYRI bir `span`: `aria-pressed` zaten secili olani
              soyluyor, sayi ise ayri bir bilgi. Tek dizgede birlestirmek
              ekran okuyucuda "Aktif nokta sekiz" gibi okunuyordu.
            */}
            {s.sayi !== undefined && (
              <span
                className={cn(
                  "ml-1.5",
                  hap
                    ? acik
                      ? "text-notr-0"
                      : "text-metin-ikincil"
                    : acik
                      ? "text-marka-600"
                      : "text-notr-600",
                )}
              >
                · {s.sayi}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * TEK SEKME HAPI — `Sekmeler gorunum="hap"` ile AYNI görünüm (UNİKOÇ, tarama-1 §2.7 madde 7).
 * Bir ekran sekme şeridini kendi düzenlemesiyle (ör. araya ek öğe koyarak) kurmak istediğinde bunu kullanır;
 * şerit olarak kullanılacaksa `Sekmeler` tercih edilir. Seçili durum yalnız renkle değil `aria-pressed` ile söylenir.
 */
export function SekmeHapi({
  secili,
  onClick,
  children,
  ikon,
}: {
  secili: boolean;
  onClick: () => void;
  children: ReactNode;
  ikon?: IkonAdi;
}) {
  return (
    <button
      type="button"
      aria-pressed={secili}
      onClick={onClick}
      className={cn(
        "rounded-rozet min-h-dokunma inline-flex items-center gap-1.5 px-4 text-sm font-semibold",
        "ease-cikis transition-[box-shadow,background-color] duration-(--sure-anlik)",
        "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        secili
          ? "text-notr-0 bg-(image:--gradyan-unk-birincil) shadow-(--golge-unk-birincil)"
          : "bg-notr-0 text-metin-koyu ring-mor-kenar hover:bg-mor-50 ring-1 ring-inset",
      )}
    >
      {ikon && <Ikon ad={ikon} className="size-4" />}
      {children}
    </button>
  );
}
